import React from 'react';
import styled, { css } from 'styled-components';

import { getColor } from '../../../styles/themeGetters';
import { Text } from '../../../Text';
import { Avatar } from '../../../Avatar';
import { Box } from '../../../Box';
import { Flex } from '../../../Flex';
import { Tag } from '../../../Tag';
import { Item } from '../types';
import { Truncate } from '../../../Truncate';
import { EntitySymbol } from '../../../EntitySymbol';
import { ConfidenceLevelDot } from '../../../ConfidenceLevelDot';

const SelectedItemRenderer = ({
  item,
  maxTextWidth,
}: {
  item: Item;
  maxTextWidth?: string;
}) => {
  switch (item.entityType) {
    case 'default':
      return (
        <Truncate
          maxWidth={maxTextWidth}
          text={item.title}
          tooltipPlacement="top"
        />
      );

    case 'confidenceLevel':
      return (
        <Flex gap={2} alignItems="center">
          <ConfidenceLevelDot value={item.confidenceLevel} />
          <Text>{item.title}</Text>
        </Flex>
      );

    case 'suffix':
      return (
        <Truncate
          maxWidth={maxTextWidth}
          text={item.title}
          tooltipPlacement="top"
        />
      );

    case 'person':
      return (
        <>
          <Tag
            label={item.title}
            truncated
            avatar={
              <Avatar
                image={
                  item.entityType === 'person' ? item.avatar?.url : undefined
                }
                fallbackText={item.avatar.fallbackText}
                type={item.entityType}
                size="xs"
              />
            }
          />
        </>
      );

    case 'team':
      return (
        <>
          <Tag
            label={item.title}
            truncated
            avatar={
              <Avatar
                fallbackText={item.title}
                type={item.entityType}
                size="xs"
              />
            }
          />
        </>
      );

    case 'businessUnit':
      return (
        <Box
          display="flex"
          flexDirection="row"
          flexGap="8px"
          alignItems="center"
        >
          <EntitySymbol variant="businessUnit" size="s" />
          <Truncate
            maxWidth={maxTextWidth}
            text={item.title}
            tooltipPlacement="top"
          />
        </Box>
      );

    case 'currency':
      return (
        <>
          <Text>
            <Text display="inline" as="span" mr={1} color="grey-dark">
              {item.suffix}
            </Text>
            {item.title}
          </Text>
        </>
      );
    default: {
      // this is to make a missing Renderer a type error. see
      // https://www.typescriptlang.org/docs/handbook/2/narrowing.html#exhaustiveness-checking
      // for how this works.
      const _exhaustiveCheck: never = item;
      return _exhaustiveCheck;
    }
  }
};

const disabledStyles = css`
  color: ${getColor('grey-light-100')};
  cursor: not-allowed;
  background: #fff;
`;

type WrapperProps = {
  $disabled?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  width: 100%;
  height: 34px;
  cursor: pointer;
  align-items: center;

  ${props => (props.$disabled ? disabledStyles : null)};
`;

type SelectedItemProps = {
  item: Item;
  isOpen: boolean;
  placeholder?: string;
  openMenu: () => void;
  disabled?: boolean;
  maxTextWidth?: string;
};

const SelectedItem = ({
  item,
  isOpen,
  placeholder,
  openMenu,
  disabled,
  maxTextWidth,
}: SelectedItemProps) => {
  return (
    <Wrapper
      onClick={!isOpen && !disabled ? openMenu : undefined}
      $disabled={disabled}
    >
      {item ? (
        <SelectedItemRenderer item={item} maxTextWidth={maxTextWidth} />
      ) : (
        <Text.Description>{placeholder}</Text.Description>
      )}
    </Wrapper>
  );
};

export { SelectedItem };
